import { useEffect, useMemo, useRef, useState } from 'react';

import useAuth from '@/hooks/useAuth';
import { Role } from '@/routes/PrivateRoute';
import { useAutocompleteCustomersLazyQuery } from '@/graphql';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import { DEBOUNCE_TIME } from '@/constants/general';
import {
  AccountRateTypeEnum,
  AutocompleteCustomersQuery,
} from '@/types/graphql';
import { CustomerStatusEnum } from '@/util/accountstatus';
import { currentAgencyVar } from '@/util/apollo/cache';
import { useReactiveVar } from '@apollo/client';

import { BillingAccountV2, OrderTemplateListItem } from '../api/types';

type EntryScreenProps = {
  billingAccounts: BillingAccountV2[];
  templates: OrderTemplateListItem[];
  selectedBillingAccountId: number | null;
  onSelectBillingAccount: (account: BillingAccountV2 | null) => void;
  onStartOrder: () => void;
  onPickTemplate: (templateId: number) => void;
};

const PROTO_TEXT_SECONDARY = '#5B5B5B';
const PROTO_CARD_BG = '#FEFEFE';

type CustomerItem =
  AutocompleteCustomersQuery['agency']['customers']['items'][0];

const PROTO_GREEN = '#44A735';
const PROTO_GREEN_DARK = '#2E8722';
const PROTO_TEXT = '#1C1C1C';
const PROTO_TEXT_MUTED = '#979797';
const PROTO_BORDER = '#DADADA';
const PROTO_BORDER_LIGHT = '#EAEAEA';
const PROTO_HOVER_BG = '#F5F5F5';
const PROTO_PAGE_BG = '#FCFCFC';
const FONT = "'Manrope', sans-serif";

const EntryScreen = ({
  billingAccounts,
  templates,
  selectedBillingAccountId,
  onSelectBillingAccount,
  onStartOrder,
  onPickTemplate,
}: EntryScreenProps) => {
  const { currentAdmin } = useAuth();
  const isTenantAdmin = currentAdmin?.role === Role.TENANT_ADMIN;

  const [billingOpen, setBillingOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientQuery, setClientQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<CustomerItem | null>(
    null,
  );
  const [tenantBillingAccounts, setTenantBillingAccounts] = useState<
    BillingAccountV2[]
  >([]);
  const [error, setError] = useState<string | null>(null);

  const billingWrapRef = useRef<HTMLDivElement>(null);
  const clientWrapRef = useRef<HTMLDivElement>(null);

  const currentAgency = useReactiveVar(currentAgencyVar);
  const debouncedQuery = useDebouncedValue(clientQuery, DEBOUNCE_TIME);
  const [fetchCustomers, { data: customersData, loading: customersLoading }] =
    useAutocompleteCustomersLazyQuery();

  // Tenant admin: search clients via GraphQL
  useEffect(() => {
    if (!isTenantAdmin || !currentAgency?.id) return;
    fetchCustomers({
      variables: {
        agencyId: currentAgency.id,
        query: debouncedQuery,
        customerStatus: [CustomerStatusEnum.APPROVED],
        rateType: [AccountRateTypeEnum.MARKUP, AccountRateTypeEnum.STATIC],
        adminFilters: { active: { value: true } },
      },
    });
  }, [isTenantAdmin, currentAgency?.id, debouncedQuery]);

  const clientItems = useMemo(
    () => customersData?.agency.customers.items ?? [],
    [customersData],
  );

  // Close popovers on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (
        billingWrapRef.current &&
        !billingWrapRef.current.contains(e.target as Node)
      ) {
        setBillingOpen(false);
      }
      if (
        clientWrapRef.current &&
        !clientWrapRef.current.contains(e.target as Node)
      ) {
        setClientOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const accountsForCurrentScope: BillingAccountV2[] = isTenantAdmin
    ? tenantBillingAccounts
    : billingAccounts;

  const selectedBillingAccount =
    accountsForCurrentScope.find((a) => a.id === selectedBillingAccountId) ||
    null;

  const handlePickClient = (customer: CustomerItem) => {
    setSelectedClient(customer);
    setClientOpen(false);
    setClientQuery('');
    onSelectBillingAccount(null);
    // Convert GraphQL accounts to BillingAccountV2 shape
    const mapped: BillingAccountV2[] = (customer.accounts || [])
      .filter((a) => a.active)
      .map<BillingAccountV2>((a) => ({
        id: Number(a.id),
        name: a.name,
        customer_id: Number(customer.id),
        active: a.active,
        tax_type: customer.jobTaxType ?? null,
        require_background_checks: customer.requireBackgroundChecks ?? null,
        rate_type:
          a.rateType === AccountRateTypeEnum.MARKUP ? 'MARKUP' : 'STATIC',
        markup_percent: a.markupPercent ?? 0,
      }));
    setTenantBillingAccounts(mapped);
  };

  const handleStart = () => {
    if (!selectedBillingAccountId) {
      setError('Please select a billing account');
      return;
    }
    setError(null);
    onStartOrder();
  };

  const canStart = !!selectedBillingAccountId;
  const showRepeatOrders = !!selectedBillingAccountId && templates.length > 0;

  return (
    <div
      style={{
        background: PROTO_PAGE_BG,
        minHeight: '100%',
        width: '100%',
        padding: '32px 24px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <p
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: PROTO_TEXT,
            fontFamily: FONT,
            margin: '0 0 24px 0',
            width: '100%',
            maxWidth: 708,
          }}
        >
          New Order
        </p>

        <div
          style={{
            background: '#fff',
            border: `1px solid ${PROTO_BORDER}`,
            borderRadius: 8,
            padding: '24px 32px',
            width: '100%',
            maxWidth: 708,
            boxSizing: 'border-box',
          }}
        >
          {isTenantAdmin && (
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'flex-end',
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: PROTO_TEXT,
                    fontFamily: FONT,
                    margin: 0,
                  }}
                >
                  Client
                </p>
                <div ref={clientWrapRef} style={{ position: 'relative' }}>
                  <div
                    onClick={() => setClientOpen((v) => !v)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      height: 34,
                      border: `1px solid ${PROTO_BORDER}`,
                      borderRadius: 8,
                      padding: '0 8px',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="text"
                      value={
                        clientOpen ? clientQuery : selectedClient?.name || ''
                      }
                      placeholder="Search for a client"
                      onChange={(e) => setClientQuery(e.target.value)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setClientOpen(true);
                      }}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: 14,
                        fontFamily: FONT,
                        color:
                          selectedClient && !clientOpen
                            ? PROTO_TEXT
                            : PROTO_TEXT_MUTED,
                        background: 'transparent',
                        cursor: 'text',
                      }}
                    />
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={PROTO_TEXT}
                      strokeWidth={2}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  {clientOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: `1px solid ${PROTO_BORDER_LIGHT}`,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 100,
                        overflow: 'hidden',
                        maxHeight: 280,
                        overflowY: 'auto',
                      }}
                    >
                      {customersLoading && clientItems.length === 0 ? (
                        <div
                          style={{
                            padding: '10px 16px',
                            fontSize: 14,
                            color: PROTO_TEXT_MUTED,
                            fontFamily: FONT,
                          }}
                        >
                          Loading...
                        </div>
                      ) : clientItems.length === 0 ? (
                        <div
                          style={{
                            padding: '10px 16px',
                            fontSize: 14,
                            color: PROTO_TEXT_MUTED,
                            fontFamily: FONT,
                          }}
                        >
                          No clients found
                        </div>
                      ) : (
                        clientItems.map((c, idx) => (
                          <div
                            key={c.id}
                            onClick={() => handlePickClient(c)}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.background =
                                PROTO_HOVER_BG)
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.background = 'transparent')
                            }
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              fontSize: 14,
                              color: PROTO_TEXT,
                              fontFamily: FONT,
                              borderBottom:
                                idx < clientItems.length - 1
                                  ? '1px solid #F5F5F5'
                                  : 'none',
                            }}
                          >
                            {c.name}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: PROTO_TEXT,
                  fontFamily: FONT,
                  margin: 0,
                }}
              >
                Billing account
              </p>
              <div ref={billingWrapRef} style={{ position: 'relative' }}>
                <div
                  onClick={() => {
                    if (isTenantAdmin && !selectedClient) return;
                    setBillingOpen((v) => !v);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 34,
                    border: `1px solid ${PROTO_BORDER}`,
                    borderRadius: 8,
                    padding: '0 8px',
                    background: '#fff',
                    cursor:
                      isTenantAdmin && !selectedClient
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: isTenantAdmin && !selectedClient ? 0.6 : 1,
                  }}
                >
                  <input
                    type="text"
                    value={selectedBillingAccount?.name || ''}
                    placeholder={
                      isTenantAdmin && !selectedClient
                        ? 'Select a client first'
                        : 'Select your billing account'
                    }
                    readOnly
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: 14,
                      fontFamily: FONT,
                      color: selectedBillingAccount
                        ? PROTO_TEXT
                        : PROTO_TEXT_MUTED,
                      background: 'transparent',
                      cursor: 'inherit',
                    }}
                  />
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={PROTO_TEXT}
                    strokeWidth={2}
                    style={{ width: 20, height: 20, flexShrink: 0 }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {billingOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: `1px solid ${PROTO_BORDER_LIGHT}`,
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 100,
                      overflow: 'hidden',
                      maxHeight: 280,
                      overflowY: 'auto',
                    }}
                  >
                    {accountsForCurrentScope.length === 0 ? (
                      <div
                        style={{
                          padding: '10px 16px',
                          fontSize: 14,
                          color: PROTO_TEXT_MUTED,
                          fontFamily: FONT,
                        }}
                      >
                        No billing accounts available
                      </div>
                    ) : (
                      accountsForCurrentScope.map((account, idx) => (
                        <div
                          key={account.id}
                          onClick={() => {
                            onSelectBillingAccount(account);
                            setBillingOpen(false);
                            setError(null);
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.background = PROTO_HOVER_BG)
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.background = 'transparent')
                          }
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            fontSize: 14,
                            color: PROTO_TEXT,
                            fontFamily: FONT,
                            borderBottom:
                              idx < accountsForCurrentScope.length - 1
                                ? '1px solid #F5F5F5'
                                : 'none',
                          }}
                        >
                          {account.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleStart}
              disabled={!canStart}
              onMouseOver={(e) => {
                if (canStart)
                  e.currentTarget.style.background = PROTO_GREEN_DARK;
              }}
              onMouseOut={(e) => {
                if (canStart) e.currentTarget.style.background = PROTO_GREEN;
              }}
              style={{
                height: 32,
                padding: '0 16px',
                background: canStart ? PROTO_GREEN : '#A8D5A1',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                fontFamily: FONT,
                cursor: canStart ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                flexShrink: 0,
                transition: 'background 0.15s',
              }}
            >
              Start order
            </button>
          </div>

          {showRepeatOrders && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                marginTop: 32,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span
                  style={{
                    fontSize: 14,
                    color: PROTO_TEXT_SECONDARY,
                    fontFamily: FONT,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Repeat orders
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: PROTO_BORDER,
                  }}
                />
              </div>
              {templates.map((template) => {
                const title =
                  template.primary_skill || `${template.order_type} order`;
                const address = template.location_label || '';
                const orderedLabel = `Ordered ${template.used_count}x`;
                return (
                  <div
                    key={template.id}
                    onClick={() => onPickTemplate(template.id)}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.borderColor = PROTO_GREEN)
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.borderColor = PROTO_BORDER)
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 16,
                      border: `1px solid ${PROTO_BORDER}`,
                      borderRadius: 8,
                      background: PROTO_CARD_BG,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={PROTO_GREEN}
                      strokeWidth={1.5}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    >
                      <path
                        d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 3v5h5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: PROTO_TEXT,
                          margin: 0,
                          fontFamily: FONT,
                        }}
                      >
                        {title}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginTop: 2,
                        }}
                      >
                        {address ? (
                          <span
                            style={{
                              fontSize: 14,
                              color: PROTO_TEXT_SECONDARY,
                              fontFamily: FONT,
                            }}
                          >
                            {address}
                          </span>
                        ) : null}
                        {address ? (
                          <span
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              background: PROTO_TEXT_SECONDARY,
                              flexShrink: 0,
                              display: 'inline-block',
                            }}
                          />
                        ) : null}
                        <span
                          style={{
                            fontSize: 14,
                            color: PROTO_TEXT_SECONDARY,
                            fontFamily: FONT,
                          }}
                        >
                          {orderedLabel}
                        </span>
                      </div>
                    </div>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={PROTO_TEXT}
                      strokeWidth={2}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                );
              })}
            </div>
          )}

          {error && (
            <p
              style={{
                fontSize: 12,
                color: '#D32F2F',
                fontFamily: FONT,
                margin: '8px 0 0 0',
              }}
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntryScreen;
